import React, { useEffect, useMemo } from 'react';
import {
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Radio,
  Row,
  Select,
  Space,
  Tooltip,
  Typography
} from 'antd';
import { Trans, useTranslation } from 'react-i18next';
import useQuotesContext from './QuotesContext';
import PreRequisitesSwitch from './PreRequisitesSwitch';
import { arrayConditionnalElement } from '../../utils/arrayConditionnalElement';
import CollapsableText from '../../utils/CollapsableText';
import { preRequisitesLayout } from '../../utils/constants/formLayout';
import numeral from 'numeral';
import i18next from 'i18next';
import { useParams } from 'react-router-dom';
import FormRadioButtonWithCustom from '../../components/FormRadioButtonWithCustom/FormRadioButtonWithCustom';
import { MARKEL_EXEMPTED_ZIPCODES } from '../../utils/markel-exemptions-zipcodes';

export const activityTypeLabelMap = {
  IT: i18next.t('contracts.rcpmk.activity_type.IT'),
  MEDIA: i18next.t('contracts.rcpmk.activity_type.MEDIA'),
  CONSULTING: i18next.t('contracts.rcpmk.activity_type.CONSULTING'),
  SERVICE: i18next.t('contracts.rcpmk.activity_type.SERVICE')
};

const activitiesType = [
  {
    label: activityTypeLabelMap.IT,
    value: 'IT'
  },
  {
    label: activityTypeLabelMap.MEDIA,
    value: 'MEDIA'
  },
  {
    label: activityTypeLabelMap.CONSULTING,
    value: 'CONSULTING'
  },
  {
    label: activityTypeLabelMap.SERVICE,
    value: 'SERVICE',
    withServiceDetails: true
  }
];

const serviceActivityDetails = [
  "Activités de soutien à l'enseignement",
  'Agence matrimoniale',
  'Agent commercial (hors immobilier)',
  'Aide à domicile',
  "Aide à l'insertion sociale",
  'Aide et/ou soutien psychologique',
  'Assistance informatique à domicile pour les particuliers',
  'Assistance administrative à domicile',
  'Chasseur de têtes',
  "Comportementaliste chien et chat/Formation au dressage et à la communication avec l'animal",
  'Conseiller en éducation / Éducateur',
  'Coordination, délivrance et suivi de services à la personnes  (non soumis à agrément)',
  'Correcteur de textes / examens',
  'Coiffeur à domicile',
  'Collecte/ Livraison de linge à domicile',
  'Conciergerie ',
  'Cours à domicile',
  'Cours de cuisine avec restauration à domicile ',
  'Cours de danse ',
  "Décorateur d'intérieur (hors architecte), paysagerie d'intérieur, home staging ",
  'Décorateur étalagiste ',
  'Éducation somatique ',
  'Enseignant - hors enseignant de l’éducation nationale',
  'Enseignement culturel',
  'Entreprise de travail temporaire',
  'Évaluation des risques et dommages',
  "Externalisation de la rédaction du document unique d'évaluation des risques professionnels",
  'Externalisation de paie, des cotisations sociales et de la gestion administrative du personnel',
  'Externalisation de tâches administratives',
  "Externalisation du management des fonctions support de l'entreprise",
  "Externalisation du suivi et développement commercial en qualité d'agent commercial/apporteur d'affaires",
  'Formateur / Organisme de formation',
  'Formation',
  'Formation / Enseignement et soutien scolaire',
  'Formation en premiers secours',
  "Formation professionnelle / Formation continue d'adultes / Conférencier",
  "Garde d'animaux / Pet sitting (hors loi borloo)",
  "Garde d'enfants de moins de 3 ans à domicile (hors loi borloo)",
  "Garde d'enfants de plus de 3 ans à domicile (hors loi borloo)",
  'Gestion pour compte de tiers de tâches administratives',
  'Gestion pour le compte de tiers de paie et gestions administrative du personnel',
  'Graphologue',
  'Graphothérapeute',
  'Guide interprète hors traduction écrite, accompagnateur de groupe hors activité touristique ',
  'Guide-conférencier',
  'Home staging',
  'Management de transition',
  "Mise à disposition d'agents et hôtesses d'accueil - hors prestation de sécurité privée",
  'Photocopie, préparation de documents et autres activités spécialisées de soutien de bureau',
  'Poste de direction par intérim ',
  "Professeur d'échecs",
  'Programmation neurolinguistique (PNL)',
  'Recherche-développement en sciences humaines et sociales',
  'Secrétaire de documentation',
  "Secrétariat d'entreprise, centre d'appels ",
  'Secrétariat, activités spécialisées de soutien de bureau, services administratifs combinés de bureau, coworking (services de bureaux partagés)',
  'Services administratifs combinés de bureau',
  'Services aux particuliers',
  'Soutien scolaire',
  "Soins et promenades d'animaux de compagnie",
  'Traduction de documents ',
  'animateur(trice) commercial(e) indépendant(e)',
  'astrologue, voyant (cabinet d’)',
  'collecte des données topographiques ( les drones ne sont pas couverts ni en RC ni en Dommages et hors géomètres experts)',
  'cours de peinture/ dessins / sculpture (hors domicile)',
  'Recherche généalogiques (hors généalogistes et notaires)',
  'salon de massage'
].map((activityDetail) => ({ value: activityDetail, label: activityDetail }));

//  TURNOVER
export const RCPMK_TURNOVER_EXEMPTION_THRESHOLD = 2000001;
const turnoverSteps = [
  {
    value: 50000,
    label: '0€ à 50 000€'
  },
  {
    value: 100000,
    label: '50 000€ à 100 000€'
  },
  {
    value: 250000,
    label: '100 000€ à 250 000€'
  },
  {
    value: 500000,
    label: '250 000€ à 500 000€'
  },
  {
    value: 750000,
    label: '500 000€ à 750 000€'
  },
  {
    value: 1000000,
    label: '750 000€ à 1 000 000€'
  },
  {
    value: 1500000,
    label: '1 000 000€ à 1 500 000€'
  },
  {
    value: 2000000,
    label: '1 500 000€ à 2 000 000€'
  },
  {
    value: 2000001,
    label: '+ de 2 000 000€'
  }
];
export const RCPMK_TURNOVER_DEFAULT_VALUE = turnoverSteps[0].value;
export { turnoverSteps as rcpmkTurnoverSteps };

// GUARANTEE LIMIT
export const RCPMK_GUARANTEE_LIMIT_EXEMPTION_THRESHOLD = 2500001;
const guaranteeLimitSteps = [
  {
    value: 50000,
    label: '50 000€',
    disabled: ({ turnover }) => turnover > 100000
  },
  {
    value: 100000,
    label: '100 000€',
    disabled: ({ turnover }) => turnover > 100000
  },
  {
    value: 250000,
    label: '250 000€'
  },
  {
    value: 500000,
    label: '500 000€'
  },
  {
    value: 750000,
    label: '750 000€'
  },
  {
    value: 1000000,
    label: '1 000 000€'
  },
  {
    value: 2500000,
    label: '2 500 000€'
  },
  {
    value: 2500001,
    label: '+ de 2 500 000€'
  }
];
export const RCPMK_GUARANTEE_LIMIT_DEFAULT_VALUE = guaranteeLimitSteps[0].value;

//  DEDUCTIBLE AMOUNT
const deductibleAmountSteps = [
  {
    value: 0,
    label: '0€'
  },
  {
    value: 250,
    label: '250€'
  },
  {
    value: 500,
    label: '500€'
  },
  {
    value: 750,
    label: '750€'
  },
  {
    value: 1000,
    label: '1 000€'
  }
];
export const RCPMK_DEDUCTIBLE_AMOUNT_DEFAULT_VALUE =
  deductibleAmountSteps[0].value;

export const RCPMK_CLAIM_AMOUNT_EXEMPTION_THRESHOLD = 1500;
export const RCPMK_CLAIM_NUMBER_EXEMPTION_THRESHOLD = 2;

export const RCPMK_EXEMPTED_ZIPCODES = MARKEL_EXEMPTED_ZIPCODES;

const useRCPMKFields = (form, supplier) => {
  const { t } = useTranslation();
  const { subPath: type } = useParams();

  const {
    exemptions,
    formValues,
    setFormValues,
    setFormFieldValue,
    enums
  } = useQuotesContext();

  const isAmendment = useMemo(() => window.location.href.search('amendment'), [
    window.location.href
  ]);

  const displayServiceActivityDetails = useMemo(() => {
    if (!formValues?.activity_type) {
      return false;
    }

    const foundActivityType = activitiesType.find(
      ({ value }) => formValues?.activity_type === value
    );

    return !!foundActivityType?.withServiceDetails;
  }, [formValues?.activity_type]);

  const selectedActivityType = useMemo(() => {
    if (!formValues?.activity_type) {
      return null;
    }

    return formValues.activity_type;
  }, [formValues?.activity_type]);

  useEffect(() => {
    if (type !== 'rcpmk') {
      return;
    }

    if (!formValues?.guarantee_limit) {
      return;
    }

    const selectedGuaranteeStep = guaranteeLimitSteps.find(
      (step) => step.value >= formValues?.guarantee_limit
    );

    if (!selectedGuaranteeStep?.disabled?.(formValues)) {
      return;
    }

    const newSelectedGuaranteeStep = guaranteeLimitSteps.find(
      (step) => !step.disabled?.(formValues)
    );

    if (newSelectedGuaranteeStep) {
      form.setFieldsValue({
        ...formValues,
        guarantee_limit: newSelectedGuaranteeStep.value
      });
      setFormFieldValue('guarantee_limit', newSelectedGuaranteeStep.value);
    }
  }, [formValues?.turnover]);

  useEffect(() => {
    if (!displayServiceActivityDetails) {
      setFormFieldValue('service_activity_details', null);
      form.setFieldValue('service_activity_details', null);
    } else if (
      displayServiceActivityDetails &&
      !formValues?.service_activity_details
    ) {
      setFormFieldValue('service_activity_details', []);
      form.setFieldValue('service_activity_details', []);
    }
  }, [displayServiceActivityDetails]);

  const rcpmkFields = [
    /*
     * STEP 1
     */
    {
      name: ['is_startup'],
      tooltip: true,
      tooltipName: 'is_startup',
      rules: [{ required: true }],
      input: <PreRequisitesSwitch />,
      help: exemptions.includes('is_startup') ? t('help.exemptions') : null,
      valuePropName: 'checked',
      step: 0
    },
    {
      name: ['activity_type'],
      tooltip: false,
      rules: [
        { required: true },
        {
          validator: (_, value) =>
            !!value
              ? Promise.resolve()
              : Promise.reject(new Error('Ce champs est requis.'))
        }
      ],
      input: (
        <Radio.Group buttonStyle="solid">
          {activitiesType.map(({ label, value }) => (
            <Radio.Button value={value}>
              <Space orientation="horizontal">{label}</Space>
            </Radio.Button>
          ))}
        </Radio.Group>
      ),
      help: exemptions.includes('activity_type') ? t('help.exemptions') : null,
      step: 0
    },
    ...arrayConditionnalElement(
      {
        name: ['service_activity_details'],
        tooltip: false,
        rules: [
          {
            validator: (_, value, callback) => {
              if (!Array.isArray(value) || value.length === 0) {
                return callback('Ce champs est requis.');
              }

              if (value.length > 3) {
                return callback(
                  'Vous ne pouvez pas sélectionner plus de 3 activités.'
                );
              }

              return callback();
            }
          }
        ],
        input: (
          <Select
            options={serviceActivityDetails}
            placeholder={t('input.select.placeholder')}
            mode="multiple"
            defaultValue={[]}
          />
        ),
        help: exemptions.includes('activity_type')
          ? t('help.exemptions')
          : null,
        step: 0
      },
      displayServiceActivityDetails
    ),
    {
      name: ['turnover'],
      tooltip: true,
      tooltipLabel: t('contracts.form.tooltip.rcpmk.turnover', {
        threshold: numeral(RCPMK_TURNOVER_EXEMPTION_THRESHOLD - 1).format()
      }),
      rules: [
        { required: true },
        {
          validator: (_, value) => {
            return formValues && value > 0
              ? Promise.resolve()
              : Promise.reject(new Error('Ce champs est requis.'));
          }
        }
      ],
      input: (
        <FormRadioButtonWithCustom
          fieldName="turnover"
          options={turnoverSteps}
          getIsCustom={({ value }) =>
            value >= RCPMK_TURNOVER_EXEMPTION_THRESHOLD &&
            formValues?.turnover >= RCPMK_TURNOVER_EXEMPTION_THRESHOLD
          }
          customInputProps={{
            min: RCPMK_TURNOVER_EXEMPTION_THRESHOLD,
            max: null
          }}
        />
      ),
      help: exemptions.includes('turnover') ? t('help.exemptions') : null,
      step: 0
    },
    {
      name: ['guarantee_limit'],
      tooltip: true,
      tooltipLabel: t('contracts.form.tooltip.rcpmk.guarantee_limit', {
        threshold: numeral(
          RCPMK_GUARANTEE_LIMIT_EXEMPTION_THRESHOLD - 1
        ).format()
      }),
      rules: [
        { required: true },
        {
          validator: (_, value) => {
            const selectedStep = guaranteeLimitSteps.find(
              (step) => step.value === value
            );

            const isDisabled = selectedStep?.disabled?.(formValues);

            return formValues && value > 0 && !isDisabled
              ? Promise.resolve()
              : Promise.reject(new Error('Ce champs est requis.'));
          }
        }
      ],
      input: (
        <FormRadioButtonWithCustom
          fieldName="guarantee_limit"
          options={guaranteeLimitSteps}
          getIsCustom={({ value }) =>
            value >= RCPMK_GUARANTEE_LIMIT_EXEMPTION_THRESHOLD &&
            formValues?.guarantee_limit >=
              RCPMK_GUARANTEE_LIMIT_EXEMPTION_THRESHOLD
          }
          customInputProps={{
            min: RCPMK_GUARANTEE_LIMIT_EXEMPTION_THRESHOLD,
            max: null
          }}
        />
      ),
      help: exemptions.includes('guarantee_limit')
        ? t('help.exemptions')
        : null,
      step: 0
    },
    {
      name: ['deductible_amount'],
      tooltip: true,
      tooltipName: 'rcpmk.deductible_amount',
      rules: [
        { required: true },
        {
          validator: (_, value) =>
            formValues && typeof value === 'number'
              ? Promise.resolve()
              : Promise.reject(new Error('Ce champs est requis.'))
        }
      ],
      input: (
        <Radio.Group buttonStyle="solid">
          {deductibleAmountSteps.map(({ label, value }) => (
            <Radio.Button value={value}>
              <Space orientation="horizontal">{label}</Space>
            </Radio.Button>
          ))}
        </Radio.Group>
      ),
      help: exemptions.includes('deductible_amount')
        ? t('help.exemptions')
        : null,
      step: 0
    },
    {
      name: ['operational_protection'],
      input: <PreRequisitesSwitch />,
      label: (
        <CollapsableText
          title={
            <Trans>
              <Typography.Title level={5}>
                {t(`contracts.form.operational_protection`)}
              </Typography.Title>
            </Trans>
          }
          text={
            <Trans>
              <Col>
                <Typography.Paragraph style={{ textTransform: 'none' }}>
                  {t(`contracts.form.operational_protection_description`)}
                </Typography.Paragraph>
                <Typography.Text style={{ textTransform: 'none' }}>
                  {t(`contracts.form.operational_protection_helper`)}
                </Typography.Text>
              </Col>
            </Trans>
          }
        />
      ),
      formattedLabel: true,
      labelAlign: 'left',
      valuePropName: 'checked',
      withDivider: true,
      step: 0
    },
    {
      name: ['cyber_events_protection'],
      input: <PreRequisitesSwitch />,
      label: (
        <CollapsableText
          title={
            <Trans>
              <Typography.Title level={5}>
                {t(`contracts.form.cyber_events_protection`)}
              </Typography.Title>
            </Trans>
          }
          text={
            <Trans>
              <Col>
                <Typography.Paragraph style={{ textTransform: 'none' }}>
                  {t(`contracts.form.cyber_events_protection_description`)}
                </Typography.Paragraph>
                <Typography.Text style={{ textTransform: 'none' }}>
                  {t(`contracts.form.cyber_events_protection_helper`)}
                </Typography.Text>
              </Col>
            </Trans>
          }
        />
      ),
      formattedLabel: true,
      labelAlign: 'left',
      valuePropName: 'checked',
      withDivider: true,
      step: 0
    },
    {
      name: ['key_man_protection'],
      input: <PreRequisitesSwitch />,
      label: (
        <CollapsableText
          title={
            <Trans>
              <Typography.Title level={5}>
                {t(`contracts.form.key_man_protection`)}
              </Typography.Title>
            </Trans>
          }
          text={
            <Trans>
              <Col>
                <Typography.Paragraph style={{ textTransform: 'none' }}>
                  {t(`contracts.form.key_man_protection_description`)}
                </Typography.Paragraph>
                <Typography.Text style={{ textTransform: 'none' }}>
                  {t(`contracts.form.key_man_protection_helper`)}
                </Typography.Text>
              </Col>
            </Trans>
          }
        />
      ),
      formattedLabel: true,
      labelAlign: 'left',
      valuePropName: 'checked',
      withDivider: true,
      step: 0
    },
    {
      name: ['management_protection'],
      input: <PreRequisitesSwitch />,
      label: (
        <CollapsableText
          title={
            <Trans>
              <Typography.Title level={5}>
                {t(`contracts.form.management_protection`)}
              </Typography.Title>
            </Trans>
          }
          text={
            <Trans>
              <Col>
                <Typography.Paragraph style={{ textTransform: 'none' }}>
                  {t(`contracts.form.management_protection_description`)}
                </Typography.Paragraph>
                <Typography.Text style={{ textTransform: 'none' }}>
                  {t(`contracts.form.management_protection_helper`)}
                </Typography.Text>
              </Col>
            </Trans>
          }
        />
      ),
      formattedLabel: true,
      labelAlign: 'left',
      valuePropName: 'checked',
      withDivider: true,
      step: 0
    },
    ...arrayConditionnalElement(
      {
        name: ['merge_protection'],
        input: <PreRequisitesSwitch />,
        label: (
          <CollapsableText
            title={
              <Trans>
                <Typography.Title level={5}>
                  {t(`contracts.form.merge_protection`)}
                </Typography.Title>
              </Trans>
            }
            text={
              <Trans>
                <Col>
                  <Typography.Paragraph style={{ textTransform: 'none' }}>
                    {t(`contracts.form.merge_protection_description`)}
                  </Typography.Paragraph>
                  <Typography.Text style={{ textTransform: 'none' }}>
                    {t(`contracts.form.merge_protection_helper`)}
                  </Typography.Text>
                </Col>
              </Trans>
            }
          />
        ),
        formattedLabel: true,
        labelAlign: 'left',
        valuePropName: 'checked',
        withDivider: true,
        step: 0
      },
      selectedActivityType === 'CONSULTING'
    ),
    {
      name: ['fraction_type'],
      rules: [{ required: true }],
      tooltip: false,
      input: (
        <Radio.Group buttonStyle="solid" disabled={isAmendment !== -1}>
          <Radio.Button value="MONTHLY">
            {t('contracts.form.options.MONTHLY')}
          </Radio.Button>
          <Radio.Button value="QUARTERLY">
            {t('contracts.form.options.QUARTERLY')}
          </Radio.Button>
          <Radio.Button value="BIANNUAL">
            {t('contracts.form.options.BIANNUAL')}
          </Radio.Button>
          <Radio.Button value="YEARLY">
            {t('contracts.form.options.YEARLY')}
          </Radio.Button>
        </Radio.Group>
      ),
      step: 0
    },

    /*
     * STEP 2
     */
    ...arrayConditionnalElement(
      [
        {
          name: ['it_forbidden_activities'],
          step: 1
        },
        {
          name: ['it_regulated_activities'],
          step: 1
        }
      ],
      selectedActivityType === 'IT'
    ),
    ...arrayConditionnalElement(
      [
        {
          name: ['media_forbidden_activities'],
          step: 1
        }
      ],
      selectedActivityType === 'MEDIA'
    ),
    ...arrayConditionnalElement(
      [
        {
          name: ['service_forbidden_activities_one'],
          step: 1
        },
        {
          name: ['service_forbidden_activities_two'],
          step: 1
        },
        {
          name: ['service_forbidden_activities_three'],
          step: 1
        }
      ],
      selectedActivityType === 'SERVICE'
    ),
    ...arrayConditionnalElement(
      [
        {
          name: ['consulting_forbidden_activities'],
          step: 1
        }
      ],
      selectedActivityType === 'CONSULTING'
    ),
    ...arrayConditionnalElement(
      {
        name: ['managment_protection_prerequisite'],
        step: 1
      },
      !!formValues?.management_protection
    ),
    ...arrayConditionnalElement(
      {
        name: ['cyber_protection_prerequisite'],
        step: 1
      },
      !!formValues?.cyber_events_protection
    ),
    ...arrayConditionnalElement(
      {
        name: ['merge_protection_prerequisite'],
        step: 1
      },
      !!formValues?.merge_protection
    ),
    {
      name: ['etablishment_outside_france'],
      step: 1
    },
    {
      name: ['limited_turnover_in_usa'],
      step: 1
    },
    {
      name: ['limited_claims_indemnity'],
      step: 1
    },
    {
      name: ['current_claim'],
      step: 1
    }
  ].map((field) => {
    if (field.step === 1) {
      field.label = <Trans>{t(`contracts.form.${field.name[0]}`)}</Trans>;
      return {
        input: <PreRequisitesSwitch />,
        labelAlign: 'left',
        valuePropName: 'checked',
        withDivider: true,
        layout: preRequisitesLayout,
        formattedLabel: true,
        ...field
      };
    }

    return field;
  });

  return {
    rcpmkFields
  };
};

export default useRCPMKFields;
